// The esv.org-style picker: click the passage title, choose a book, then a
// chapter. A reference typed into the box at the top jumps straight there.
import { BOOKS, bookById, parseRef, RefError, toDisplay, toSlug, type Book, type Ref } from "@bibleunleashed/shared";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "wouter";

export function PassagePicker({ current, onClose }: { current: Ref; onClose: () => void }) {
  const [, navigate] = useLocation();
  const [book, setBook] = useState<Book | undefined>(() => bookById(current.book));
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    input.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    // composedPath, not contains(): React re-renders synchronously on click, so
    // a button that unmounted itself (e.g. "← Books") is gone before this runs.
    const onClick = (e: MouseEvent) => {
      if (box.current && !e.composedPath().includes(box.current)) onClose();
    };
    document.addEventListener("keydown", onKey);
    // Deferred so the click that opened the picker does not also close it.
    const t = setTimeout(() => document.addEventListener("click", onClick), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.removeEventListener("click", onClick);
    };
  }, [onClose]);

  const go = (ref: Ref) => {
    navigate(`/commentary/${toSlug(ref)}${ref.verse ? `#v${ref.verse}` : ""}`);
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    try {
      go(parseRef(query));
    } catch (err) {
      setError(err instanceof RefError ? err.message : "Try a reference like Nehemiah 1:3");
    }
  };

  const bookBtn = (b: Book) => (
    <button
      key={b.id}
      onClick={() => setBook(b)}
      className={`truncate rounded px-2 py-[5px] text-left text-[13px] hover:bg-paper-3 hover:text-ink ${b.id === current.book ? "font-semibold text-ink" : "text-ink-2"}`}
    >
      {b.name}
    </button>
  );

  return (
    <div
      ref={box}
      role="dialog"
      aria-label="Choose a passage"
      className="absolute top-[52px] left-1/2 z-30 w-[min(680px,calc(100vw-16px))] -translate-x-1/2 rounded-lg border border-rule bg-paper shadow-pop"
    >
      <form onSubmit={submit} className="border-b border-rule p-3">
        <input
          ref={input}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setError(null);
          }}
          placeholder="Go to a passage, e.g. Nehemiah 1:3 or 1 John 2"
          aria-label="Go to a passage"
          className="w-full rounded-md border border-rule bg-paper-2 px-3 py-2 font-serif text-[15px] text-ink placeholder:text-ink-3 focus:border-accent focus-visible:outline-none"
        />
        {error && <p className="mt-2 text-[12.5px] text-accent">{error}</p>}
      </form>

      {book ? (
        <div className="p-3">
          <div className="mb-2 flex items-center gap-3">
            <button onClick={() => setBook(undefined)} className="text-[12px] uppercase tracking-[0.06em] text-ink-3 hover:text-ink">← Books</button>
            <span className="font-serif text-[16px] font-semibold">{book.name}</span>
            <span className="text-[12px] text-ink-3">{book.chapters === 1 ? "1 chapter" : `${book.chapters} chapters`}</span>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(40px,1fr))] gap-1">
            {Array.from({ length: book.chapters }, (_, i) => i + 1).map((c) => {
              const on = book.id === current.book && c === current.chapter;
              return (
                <button
                  key={c}
                  onClick={() => go({ book: book.id, chapter: c })}
                  aria-current={on ? "page" : undefined}
                  className={`h-9 rounded text-[13px] tabular-nums ${on ? "bg-accent text-white" : "text-ink-2 hover:bg-paper-3 hover:text-ink"}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="grid max-h-[70vh] grid-cols-1 gap-x-6 overflow-auto p-3 sm:grid-cols-[3fr_2fr]">
          <div>
            <div className="mb-1 px-2 text-[11px] uppercase tracking-[0.08em] text-ink-3">Old Testament</div>
            <div className="grid grid-cols-3">{BOOKS.filter((b) => b.testament === "OT").map(bookBtn)}</div>
          </div>
          <div>
            <div className="mb-1 px-2 text-[11px] uppercase tracking-[0.08em] text-ink-3">New Testament</div>
            <div className="grid grid-cols-3 sm:grid-cols-2">{BOOKS.filter((b) => b.testament === "NT").map(bookBtn)}</div>
          </div>
        </div>
      )}
      <div className="border-t border-rule px-3 py-2 text-[11.5px] text-ink-3">Reading {toDisplay({ book: current.book, chapter: current.chapter })}</div>
    </div>
  );
}
